import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Users, Video } from "lucide-react";

export const NextVisitBanner = ({ visit }) => {
  if (!visit) return null;
  
  return (
    <Card className="bg-white shadow-sm overflow-hidden" data-testid="next-visit-card">
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Your Next Visit
        </h3>
      </div>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-semibold text-slate-800">
              {visit.type_name || visit.visit_type}
            </p>
            <p className="text-slate-600">{visit.formatted_date || visit.date}</p>
            <p className="text-slate-500 text-sm">
              {visit.start_time} - {visit.end_time}
            </p>
            <p className="text-teal-600 text-sm mt-1">with {visit.staff_name}</p>
          </div>
          <div className="text-right">
            {visit.is_today && <Badge className="bg-green-100 text-green-700">Today!</Badge>}
            {visit.is_tomorrow && <Badge className="bg-blue-100 text-blue-700">Tomorrow</Badge>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const VisitItem = ({ visit, index }) => (
  <div
    className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
    data-testid={`upcoming-visit-${index}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3">
        <div 
          className="w-2 h-full min-h-[48px] rounded-full"
          style={{ backgroundColor: visit.color || "#14b8a6" }}
        />
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-800">{visit.type_name || visit.visit_type}</p>
            {visit.is_today && <Badge className="bg-green-100 text-green-700 text-xs">Today</Badge>}
            {visit.is_tomorrow && <Badge className="bg-blue-100 text-blue-700 text-xs">Tomorrow</Badge>}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Calendar className="w-3 h-3 text-slate-400" />
            <p className="text-sm text-slate-600">{visit.formatted_date || visit.date}</p>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-slate-400" />
            <p className="text-sm text-slate-500">
              {visit.start_time} - {visit.end_time || ""}
              {visit.duration && <span className="text-slate-400"> ({visit.duration} min)</span>}
            </p>
          </div>
          {visit.staff_name && (
            <div className="flex items-center gap-2 mt-1">
              <Users className="w-3 h-3 text-teal-500" />
              <p className="text-sm text-teal-600">{visit.staff_name}</p>
            </div>
          )}
          {visit.notes && <p className="text-xs text-slate-400 mt-1 italic">{visit.notes}</p>}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {visit.status && (
          <Badge className={
            visit.status === "confirmed" ? "bg-green-100 text-green-700" :
            visit.status === "pending" ? "bg-yellow-100 text-yellow-700" :
            "bg-slate-100 text-slate-700"
          }>
            {visit.status}
          </Badge>
        )}
        {visit.telehealth && (
          <Badge className="bg-blue-100 text-blue-700">
            <Video className="w-3 h-3 mr-1" />
            Video
          </Badge>
        )}
      </div>
    </div>
    {visit.preparation_notes && (
      <div className="mt-2 p-2 bg-teal-50 rounded text-xs text-teal-700">
        <span className="font-medium">Prep:</span> {visit.preparation_notes}
      </div>
    )}
  </div>
);

export const UpcomingVisitsCard = ({ visits = [], maxVisible = 5 }) => (
  <Card className="bg-white shadow-sm" data-testid="upcoming-schedule">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <Calendar className="w-5 h-5 text-teal-600" />
        Upcoming Visits
      </CardTitle>
    </CardHeader>
    <CardContent>
      {visits.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No upcoming visits scheduled</p>
          <Button variant="link" className="text-teal-600 mt-2">Request a visit</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {visits.slice(0, maxVisible).map((visit, index) => (
            <VisitItem key={visit.id || index} visit={visit} index={index} />
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default UpcomingVisitsCard;
